const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

type StoredRecord<T> = {
  data: T;
  savedAt: number;
};

type NamespaceConfig = {
  namespace: string;
  ttlMs?: number;
};

export type LocalStorageNamespace<T> = {
  save: (value: T, id?: string) => void;
  get: (id?: string) => T | null;
  clear: (id?: string) => void;
};

export function createLocalStorageNamespace<T>(
  config: NamespaceConfig
): LocalStorageNamespace<T> {
  const ttlMs = config.ttlMs ?? DEFAULT_TTL_MS;
  const keyFor = (id?: string) =>
    id ? `${config.namespace}_${id}` : config.namespace;

  const clear = (id?: string) => {
    try {
      localStorage.removeItem(keyFor(id));
    } catch (error) {
      console.error(`Failed to clear ${keyFor(id)}:`, error);
    }
  };

  return {
    save(value: T, id?: string) {
      try {
        const record: StoredRecord<T> = { data: value, savedAt: Date.now() };
        localStorage.setItem(keyFor(id), JSON.stringify(record));
      } catch (error) {
        console.error(`Failed to save ${keyFor(id)}:`, error);
      }
    },

    get(id?: string): T | null {
      try {
        const raw = localStorage.getItem(keyFor(id));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredRecord<T>;
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          typeof parsed.savedAt !== "number"
        ) {
          clear(id);
          return null;
        }
        if (Date.now() - parsed.savedAt > ttlMs) {
          clear(id);
          return null;
        }
        return parsed.data;
      } catch (error) {
        console.error(`Failed to read ${keyFor(id)}:`, error);
        clear(id);
        return null;
      }
    },

    clear,
  };
}
